import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { QUERY_KEY_SETTINGS_ADMIN_PANEL } from '@/lib/api/constants'

export interface ServiceAccountChild {
  id: string
  companyName: string
  nip: string | null
}

export interface ServiceAccount {
  id: string
  companyName: string
  nip: string | null
  // 0 = Manual (panel/NIP), 1 = Stripe (legacy), 2 = Przelewy24
  billingProvider: number
  // Whether the current user is an owner of this account. Only populated by the owner-facing
  // "my accounts" endpoint; the global-admin listing leaves it undefined.
  isOwner?: boolean
  // The account company has its own database — a candidate for splitting.
  hasDatabase?: boolean
  children: ServiceAccountChild[]
}

export interface ServiceCompanySplitUser {
  userId: number
  login: string
  firstName: string | null
  surname: string | null
  roles: string[]
  isRosterMember: boolean
  isRosterOwner: boolean
  // Login or e-mail equals the company's e-mail for files — the default pick.
  matchesAccountEmail: boolean
  suggestWholeAccount: boolean
}

export interface ServiceCompanySplitPreview {
  serviceAccountId: string
  companyName: string
  nip: string | null
  childCount: number
  blockedReason: string | null
  groupRoleUserCount: number
  users: ServiceCompanySplitUser[]
}

// A member of a service account. Owners manage the roster and act as a company admin across
// the account's scope; members get operational access only. Neither holds a CompanyRole —
// access is derived server-side from the membership.
export interface ServiceAccountUser {
  userId: number
  login: string
  firstName: string | null
  surname: string | null
  isOwner: boolean
  locked: boolean
  emailVerified: boolean
  hasKeycloakAccount: boolean
  lastLogin: string | null
  created: string | null
}

// A minimal existing-user hit for the roster "add user" typeahead.
export interface UserSearchResult {
  id: number
  login: string
  firstName: string | null
  surname: string | null
}

export const SERVICE_ACCOUNTS_QUERY_KEY = ['admin', 'serviceAccounts'] as const
const attachableQueryKey = (search: string) =>
  ['admin', 'serviceAccounts', 'attachable', search] as const
// Shares the SERVICE_ACCOUNTS_QUERY_KEY prefix, so invalidating that refreshes rosters too.
const serviceAccountUsersQueryKey = (accountId: string) =>
  ['admin', 'serviceAccounts', accountId, 'users'] as const
const splitPreviewQueryKey = (accountId: string) =>
  ['admin', 'serviceAccounts', accountId, 'split'] as const

// The service accounts the CURRENT user belongs to (each with `isOwner`). Powers the owner-facing
// panel; unlike useQueryServiceAccounts (all accounts, global admin), membership is the authorization.
export function useQueryMyServiceAccounts({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<ServiceAccount[]>({
    queryKey: ['serviceAccounts', 'mine'],
    queryFn: async () => {
      const res = await axiosInstance.get('/config-api/company/service-accounts/mine')
      return res.data
    },
    staleTime: MINUTE_IN_MS * 5,
    enabled,
  })
}

// Global-admin listing of every service account. `enabled` keeps it off for users who
// would only get a 403 — the owner panel turns it on solely as an admin fallback.
export function useQueryServiceAccounts({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<ServiceAccount[]>({
    queryKey: SERVICE_ACCOUNTS_QUERY_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get('/config-api/company/service-accounts')
      return res.data
    },
    enabled,
  })
}

export function useQueryAttachableCompanies(search: string) {
  return useQuery<ServiceAccountChild[]>({
    queryKey: attachableQueryKey(search),
    queryFn: async () => {
      const res = await axiosInstance.get('/config-api/company/attachable', {
        params: search ? { search } : undefined,
      })
      return res.data
    },
  })
}

export function useMutationAttachCompany() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { companyId: string; tenantId: string }>({
    mutationFn: async ({ companyId, tenantId }) => {
      const res = await axiosInstance.post(`/config-api/company/${companyId}/tenant`, {
        tenantId,
      })
      return res.data
    },
    onSuccess: () => {
      // Invalidating the service-accounts key also refreshes the attachable lists,
      // which share its prefix.
      queryClient.invalidateQueries({ queryKey: SERVICE_ACCOUNTS_QUERY_KEY })
    },
  })
}

export function useMutationDetachCompany() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { companyId: string }>({
    mutationFn: async ({ companyId }) => {
      const res = await axiosInstance.post(`/config-api/company/${companyId}/tenant/remove`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SERVICE_ACCOUNTS_QUERY_KEY })
    },
  })
}

// Moves a company nested under one of the account's companies up to the account itself.
export function useMutationMoveCompanyToServiceAccount() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { companyId: string }>({
    mutationFn: async ({ companyId }) => {
      const res = await axiosInstance.post(
        `/config-api/company/${companyId}/tenant/move-to-service-account`,
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SERVICE_ACCOUNTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['config.companies'] })
    },
  })
}

// Who gets which access if the account is split (global admin only).
export function useQueryServiceCompanySplitPreview(accountId: string) {
  return useQuery<ServiceCompanySplitPreview>({
    queryKey: splitPreviewQueryKey(accountId),
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/config-api/company/service-accounts/${accountId}/split`,
      )
      return res.data
    },
    staleTime: 0,
  })
}

// Moves the account's own database company and its children under a new, database-less
// service account. Returns the new account id.
export function useMutationSplitServiceCompany() {
  const queryClient = useQueryClient()
  return useMutation<
    { serviceAccountId: string },
    unknown,
    { accountId: string; wholeAccountUserIds: number[] }
  >({
    mutationFn: async ({ accountId, wholeAccountUserIds }) => {
      const res = await axiosInstance.post(
        `/config-api/company/service-accounts/${accountId}/split`,
        { wholeAccountUserIds },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SERVICE_ACCOUNTS_QUERY_KEY })
      // The former account is now a plain company and a new one exists: the company list
      // (sidebar, users tables) and the edit screen would otherwise show the old type.
      queryClient.invalidateQueries({ queryKey: ['config.companies'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'editCompany'] })
    },
  })
}

// --- Service account roster ---
// Readable/writable by a global admin or by an owner of the account.

export function useQueryServiceAccountUsers(accountId: string | null) {
  return useQuery<ServiceAccountUser[]>({
    queryKey: serviceAccountUsersQueryKey(accountId ?? ''),
    enabled: !!accountId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/config-api/company/service-accounts/${accountId}/users`,
      )
      return res.data
    },
  })
}

// Typeahead over existing users (global admin only — the admin panel is). Reuses the admin
// user-list endpoint; the roster panel adds a picked user directly by id, or invites a typed
// email for someone who doesn't have an account yet.
export function useQueryUserSearch(search: string) {
  const trimmed = search.trim()
  return useQuery<UserSearchResult[]>({
    queryKey: ['admin', 'serviceAccounts', 'userSearch', trimmed],
    enabled: trimmed.length >= 2,
    queryFn: async () => {
      const res = await axiosInstance.get('/config-api/user/list', {
        params: { paged: true, take: 8, skip: 0, searchValue: trimmed },
      })
      const users = res.data?.users ?? []
      return users.map((u: any) => ({
        id: u.id,
        login: u.login,
        firstName: u.firstName ?? null,
        surname: u.surname ?? null,
      }))
    },
  })
}

// Adds an existing user, or flips the owner flag when they are already a member.
export function useMutationSetServiceAccountUser(accountId: string) {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { userId: number; isOwner: boolean }>({
    mutationFn: async ({ userId, isOwner }) => {
      const res = await axiosInstance.post(
        `/config-api/company/service-accounts/${accountId}/users`,
        { userId, isOwner },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceAccountUsersQueryKey(accountId) })
    },
  })
}

// Invites by email. Covers both cases: someone without an account is provisioned in Keycloak
// and mailed a password-setup link, while an existing user is simply added to the roster.
export function useMutationInviteServiceAccountUser(accountId: string) {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { login: string; isOwner: boolean }>({
    mutationFn: async ({ login, isOwner }) => {
      const res = await axiosInstance.post(
        `/config-api/company/service-accounts/${accountId}/users/invite`,
        { login, isOwner },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceAccountUsersQueryKey(accountId) })
    },
  })
}

export function useMutationRemoveServiceAccountUser(accountId: string) {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { userId: number }>({
    mutationFn: async ({ userId }) => {
      const res = await axiosInstance.post(
        `/config-api/company/service-accounts/${accountId}/users/remove`,
        { userId },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceAccountUsersQueryKey(accountId) })
    },
  })
}

// --- Dostępy: role ludzi we wszystkich firmach konta ---

export interface ServiceAccountAccessRole {
  id: number
  name: string
}

export interface ServiceAccountAccessUser {
  userId: number
  login: string
  firstName: string | null
  surname: string | null
  hasKeycloakAccount: boolean
  lastLogin: string | null
  rosterRole: 'owner' | 'member' | null
  accountRoles: ServiceAccountAccessRole[]
  companyRoles: Array<{ companyId: string; roles: ServiceAccountAccessRole[] }>
  /** Firmy z aktywnym profilem - także takie, w których osoba nie ma już roli. */
  profileCompanyIds: string[]
}

export interface ServiceAccountAccess {
  companies: Array<{ id: string; name: string }>
  users: ServiceAccountAccessUser[]
}

export interface ServiceAccountAccessEntry {
  login: string
  firstName?: string
  surname?: string
  companyId: string
  /** Pełen zestaw ról w firmie; pusty usuwa osobę z firmy. */
  roleIds: number[]
}

export interface ServiceAccountAccessResult {
  index: number
  login: string
  companyId: string
  status: 'saved' | 'removed' | 'error'
  code: string | null
}

const serviceAccountAccessQueryKey = (accountId: string) =>
  ['admin', 'serviceAccounts', accountId, 'access'] as const

export function useQueryServiceAccountAccess(accountId: string) {
  return useQuery<ServiceAccountAccess>({
    queryKey: serviceAccountAccessQueryKey(accountId),
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/config-api/company/service-accounts/${accountId}/access`,
      )
      return res.data
    },
  })
}

export function useMutationSetServiceAccountAccess(accountId: string) {
  const queryClient = useQueryClient()
  return useMutation<ServiceAccountAccessResult[], unknown, ServiceAccountAccessEntry[]>({
    mutationFn: async (entries) => {
      const res = await axiosInstance.post(
        `/config-api/company/service-accounts/${accountId}/access`,
        { entries },
      )
      return res.data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: serviceAccountAccessQueryKey(accountId) })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_SETTINGS_ADMIN_PANEL, 'users'] })
    },
  })
}
